import React, { useState, useEffect, useCallback, useRef } from "react";
import { Radio, Users, UserCheck, UserCog, FileText, Globe, Clock, Eye } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import api from "@/lib/api";

const LIVE_POLL_MS = 30000;

const PATH_LABEL = (p) => {
  if (!p) return "/";
  const map = {
    "/": "Home",
    "/pricing": "Pricing",
    "/login": "Login",
    "/signup": "Sign up",
    "/contact": "Contact",
  };
  return map[p] || p;
};

const relTime = (iso) => {
  if (!iso) return "—";
  const diff = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (diff < 5) return "just now";
  if (diff < 60) return `${diff}s ago`;
  const m = Math.floor(diff / 60);
  return `${m}m ago`;
};

const LiveStat = ({ icon: Icon, label, value, accent = "text-primary", testid }) => (
  <div className="rounded-lg border bg-card p-4" data-testid={testid}>
    <div className="flex items-center justify-between">
      <p className="text-xs text-muted-foreground">{label}</p>
      <Icon className={`h-4 w-4 ${accent}`} />
    </div>
    <p className="mt-1.5 font-display text-2xl font-semibold tabular-nums" data-testid={`${testid}-value`}>{value}</p>
  </div>
);

export const LiveVisitors = ({ title = "Live Visitors" }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(true); // tracking heartbeat ok
  const tick = useRef(0);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/live-visitors");
      setData(r.data);
      setActive(true);
    } catch (e) {
      setActive(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(() => { tick.current += 1; load(); }, LIVE_POLL_MS);
    return () => clearInterval(id);
  }, [load]);

  const liveNow = data?.live_now ?? 0;
  const pages = data?.current_pages || [];
  const sources = data?.sources || [];
  const visitors = data?.visitors || [];

  return (
    <div className="rounded-xl border bg-card p-5" data-testid="live-visitors">
      {/* Header + status indicator */}
      <div className="flex flex-wrap items-center gap-2">
        <Radio className="h-4 w-4 text-primary" />
        <h3 className="font-display text-base font-semibold">{title}</h3>
        <span
          className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${
            active
              ? "bg-[hsl(var(--risk-low-bg))] text-[hsl(var(--risk-low))]"
              : "bg-[hsl(var(--risk-medium-bg))] text-[hsl(var(--risk-medium))]"
          }`}
          data-testid="live-tracking-indicator"
        >
          <span className="relative flex h-2 w-2">
            {active && <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[hsl(var(--risk-low))] opacity-75" />}
            <span className={`relative inline-flex h-2 w-2 rounded-full ${active ? "bg-[hsl(var(--risk-low))]" : "bg-[hsl(var(--risk-medium))]"}`} />
          </span>
          {active ? "Live tracking active" : "Reconnecting…"}
        </span>
        <span className="ml-auto flex items-center gap-1 text-xs text-muted-foreground">
          <Clock className="h-3.5 w-3.5" />
          Active in last {data?.window_minutes ?? 5} min · last activity {relTime(data?.last_active)}
        </span>
      </div>

      {loading ? (
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-20 rounded-lg" />)}
        </div>
      ) : (
        <>
          {/* Core counts */}
          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
            <LiveStat icon={Users} label="Live visitors right now" value={liveNow} accent="text-[hsl(var(--risk-low))]" testid="live-now" />
            <LiveStat icon={UserCheck} label="Live registered users" value={data?.live_registered ?? 0} testid="live-registered" />
            <LiveStat icon={UserCog} label="Live anonymous visitors" value={data?.live_anonymous ?? 0} accent="text-muted-foreground" testid="live-anonymous" />
          </div>

          {liveNow === 0 ? (
            <div className="mt-4 flex flex-col items-center justify-center rounded-lg border border-dashed py-8 text-center" data-testid="live-empty">
              <Eye className="h-7 w-7 text-muted-foreground/50" />
              <p className="mt-2 text-sm font-medium">No visitors active right now</p>
              <p className="mt-0.5 text-xs text-muted-foreground">Live activity from the last {data?.window_minutes ?? 5} minutes appears here in real time.</p>
            </div>
          ) : (
            <div className="mt-4 grid gap-4 lg:grid-cols-2">
              {/* Current pages */}
              <div className="rounded-lg border p-4" data-testid="live-current-pages">
                <p className="mb-2 flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                  <FileText className="h-3.5 w-3.5" /> Current pages being viewed
                </p>
                <ul className="space-y-1.5">
                  {pages.map((p) => (
                    <li key={p.path} className="flex items-center justify-between gap-3 text-sm" data-testid="live-page-row">
                      <span className="truncate text-foreground" title={p.path}>{PATH_LABEL(p.path)}</span>
                      <Badge variant="secondary" className="shrink-0 tabular-nums">{p.viewers}</Badge>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Traffic sources */}
              <div className="rounded-lg border p-4" data-testid="live-sources">
                <p className="mb-2 flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                  <Globe className="h-3.5 w-3.5" /> Traffic source
                </p>
                <ul className="space-y-1.5">
                  {sources.map((s) => (
                    <li key={s.source} className="flex items-center justify-between gap-3 text-sm" data-testid="live-source-row">
                      <span className="truncate text-foreground" title={s.source}>{s.source}</span>
                      <Badge variant="secondary" className="shrink-0 tabular-nums">{s.visitors}</Badge>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* Recent activity stream */}
          {visitors.length > 0 && (
            <div className="mt-4" data-testid="live-stream">
              <p className="mb-2 text-xs font-medium text-muted-foreground">Recent activity</p>
              <div className="divide-y rounded-lg border">
                {visitors.slice(0, 8).map((vz, i) => (
                  <div key={i} className="flex items-center gap-3 px-3 py-2 text-sm" data-testid="live-visitor-row">
                    <Badge
                      className={vz.type === "registered" ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"}
                    >
                      {vz.type === "registered" ? "Registered" : "Anonymous"}
                    </Badge>
                    <span className="min-w-0 flex-1 truncate text-muted-foreground" title={vz.path}>{PATH_LABEL(vz.path)}</span>
                    <span className="hidden shrink-0 text-xs text-muted-foreground sm:inline">{vz.source}</span>
                    <span className="shrink-0 text-xs text-muted-foreground">{relTime(vz.last_active)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};
